import { cn } from '@/lib/utils';

const ViewWidth = 480;
const FacadeLeft = 48;
const FacadeTop = 76;
const FacadeWidth = 384;
const ColumnCount = 6;
const FloorHeight = 88;
const SpandrelHeight = 22;
const MullionWidth = 4;
const ParapetHeight = 16;
const GroundHeight = 44;
const ColumnWidth = FacadeWidth / ColumnCount;
const FacadeRight = FacadeLeft + FacadeWidth;
const Graphite = '#262b2f';

const ReflectionBandShapes = [
  { leftTop: 0.68, rightTop: 0.09, thickness: 0.21, opacity: 0.3 },
  { leftTop: 0.96, rightTop: 0.37, thickness: 0.07, opacity: 0.18 },
];

function toPoints(coordinates) {
  return coordinates.map(([x, y]) => `${x},${y}`).join(' ');
}

function buildFacadeGeometry(variantName, floorCount) {
  const facadeHeight = floorCount * FloorHeight;
  const facadeBottom = FacadeTop + facadeHeight;
  const floorIndexes = Array.from({ length: floorCount }, (_, floorIndex) => floorIndex);
  const columnIndexes = Array.from({ length: ColumnCount }, (_, columnIndex) => columnIndex);

  return {
    variantName,
    viewHeight: facadeBottom + GroundHeight,
    facadeHeight,
    facadeBottom,
    gradientIds: {
      sky: `facade-${variantName}-sky`,
      glass: `facade-${variantName}-glass`,
      glassBright: `facade-${variantName}-glass-bright`,
      spandrel: `facade-${variantName}-spandrel`,
      shade: `facade-${variantName}-shade`,
      facadeClip: `facade-${variantName}-clip`,
    },
    modules: floorIndexes.flatMap((floorIndex) =>
      columnIndexes.map((columnIndex) => ({
        key: `${floorIndex}-${columnIndex}`,
        left: FacadeLeft + columnIndex * ColumnWidth,
        top: FacadeTop + floorIndex * FloorHeight,
        isBright: (floorIndex * 2 + columnIndex) % 5 === 3,
      }))
    ),
    mullionPositions: Array.from({ length: ColumnCount + 1 }, (_, lineIndex) => FacadeLeft + lineIndex * ColumnWidth),
    floorLinePositions: Array.from({ length: floorCount + 1 }, (_, lineIndex) => FacadeTop + lineIndex * FloorHeight),
    reflectionBands: ReflectionBandShapes.map((bandShape) => {
      const leftTop = FacadeTop + bandShape.leftTop * facadeHeight;
      const rightTop = FacadeTop + bandShape.rightTop * facadeHeight;
      const thickness = bandShape.thickness * facadeHeight;
      return {
        points: toPoints([
          [FacadeLeft, leftTop],
          [FacadeRight, rightTop],
          [FacadeRight, rightTop + thickness],
          [FacadeLeft, leftTop + thickness],
        ]),
        opacity: bandShape.opacity,
      };
    }),
  };
}

const FacadeVariants = [
  { geometry: buildFacadeGeometry('compact', 3), className: 'block aspect-[5/4] lg:hidden' },
  { geometry: buildFacadeGeometry('full', 5), className: 'hidden aspect-[6/7] lg:block' },
];

function gradientUrl(gradientId) {
  return `url(#${gradientId})`;
}

function FacadeDrawing({ geometry, className }) {
  const { gradientIds } = geometry;

  return (
    <svg aria-hidden="true" viewBox={`0 0 ${ViewWidth} ${geometry.viewHeight}`} className={cn('w-full', className)}>
      <defs>
        <linearGradient id={gradientIds.sky} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f6fafa" />
          <stop offset="1" stopColor="#e2edef" />
        </linearGradient>
        <linearGradient id={gradientIds.glass} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#d3e4e7" />
          <stop offset="0.6" stopColor="#a5c2c8" />
          <stop offset="1" stopColor="#89abb3" />
        </linearGradient>
        <linearGradient id={gradientIds.glassBright} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#f2f8f8" />
          <stop offset="1" stopColor="#c6dce0" />
        </linearGradient>
        <linearGradient id={gradientIds.spandrel} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#5f6f76" />
          <stop offset="1" stopColor="#48565c" />
        </linearGradient>
        <linearGradient id={gradientIds.shade} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={Graphite} stopOpacity="0" />
          <stop offset="1" stopColor={Graphite} stopOpacity="0.2" />
        </linearGradient>
        <clipPath id={gradientIds.facadeClip}>
          <rect x={FacadeLeft} y={FacadeTop} width={FacadeWidth} height={geometry.facadeHeight} />
        </clipPath>
      </defs>

      <rect width={ViewWidth} height={geometry.viewHeight} fill={gradientUrl(gradientIds.sky)} />

      <g clipPath={gradientUrl(gradientIds.facadeClip)}>
        {geometry.modules.map((facadeModule) => (
          <g key={facadeModule.key}>
            <rect
              x={facadeModule.left}
              y={facadeModule.top}
              width={ColumnWidth}
              height={SpandrelHeight}
              fill={gradientUrl(gradientIds.spandrel)}
            />
            <rect
              x={facadeModule.left}
              y={facadeModule.top + SpandrelHeight}
              width={ColumnWidth}
              height={FloorHeight - SpandrelHeight}
              fill={gradientUrl(facadeModule.isBright ? gradientIds.glassBright : gradientIds.glass)}
            />
          </g>
        ))}
        {geometry.reflectionBands.map((reflectionBand) => (
          <polygon key={reflectionBand.points} points={reflectionBand.points} fill="#ffffff" fillOpacity={reflectionBand.opacity} />
        ))}
        <rect x={FacadeLeft} y={FacadeTop} width={FacadeWidth} height={geometry.facadeHeight} fill={gradientUrl(gradientIds.shade)} />
      </g>

      {geometry.floorLinePositions.map((lineTop) => (
        <rect key={`floor-${lineTop}`} x={FacadeLeft} y={lineTop - 2} width={FacadeWidth} height="4" fill={Graphite} />
      ))}
      {geometry.floorLinePositions.slice(0, -1).map((lineTop) => (
        <rect
          key={`transom-${lineTop}`}
          x={FacadeLeft}
          y={lineTop + SpandrelHeight - 1}
          width={FacadeWidth}
          height="2"
          fill={Graphite}
          fillOpacity="0.85"
        />
      ))}
      {geometry.mullionPositions.map((lineLeft) => (
        <rect
          key={`mullion-${lineLeft}`}
          x={lineLeft - MullionWidth / 2}
          y={FacadeTop - 2}
          width={MullionWidth}
          height={geometry.facadeHeight + 4}
          fill={Graphite}
        />
      ))}

      <rect x={FacadeLeft - 10} y={FacadeTop - ParapetHeight} width={FacadeWidth + 20} height={ParapetHeight} fill="#3b464c" />
      <rect x={FacadeLeft - 10} y={FacadeTop - ParapetHeight} width={FacadeWidth + 20} height="3" fill={Graphite} />
      <rect y={geometry.facadeBottom} width={ViewWidth} height={GroundHeight} fill="#d2dfe1" />
      <rect y={geometry.facadeBottom} width={ViewWidth} height="2" fill={Graphite} fillOpacity="0.5" />
    </svg>
  );
}

export function FacadeIllustration() {
  return (
    <div className="overflow-hidden rounded-lg border border-grid bg-paper shadow-[0_1px_0_var(--color-grid),0_24px_48px_-32px_rgb(38_43_47/0.45)]">
      {FacadeVariants.map((facadeVariant) => (
        <FacadeDrawing key={facadeVariant.geometry.variantName} geometry={facadeVariant.geometry} className={facadeVariant.className} />
      ))}
    </div>
  );
}